import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Mail, Image, Users, TrendingUp, Plus, Rocket, Check, X, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';
import SimpleTooltip from '../components/SimpleTooltip';
import FreemiumOnboarding from '../components/FreemiumOnboarding';
import { toast } from 'sonner';

const GO_LIVE_CHECKLIST = [
  { id: 'pages', label: 'Created and published key pages (Home, About, Contact)' },
  { id: 'branding', label: 'Uploaded logo and set brand colors in Site Theme' },
  { id: 'navigation', label: 'Set up header and footer navigation menus' },
  { id: 'content', label: 'Added content to pages (text, images, etc.)' },
  { id: 'domain', label: 'Have a custom domain ready (e.g., myschool.com.au)' },
  { id: 'tested', label: 'Previewed and tested pages on different devices' },
];

const GoLiveModal = ({ isOpen, onClose }) => {
  const [checkedItems, setCheckedItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleCheckChange = (itemId, checked) => {
    if (checked) {
      setCheckedItems([...checkedItems, itemId]);
    } else {
      setCheckedItems(checkedItems.filter(id => id !== itemId));
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const completedLabels = GO_LIVE_CHECKLIST
        .filter(item => checkedItems.includes(item.id))
        .map(item => item.label);
      
      await api.post('/tenant/go-live-request', {
        checklist_completed: completedLabels,
        notes: notes || null
      });
      
      setSubmitted(true);
      toast.success('Go Live request submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <Rocket className="w-5 h-5 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Ready to Go Live?</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Request Submitted!</h3>
              <p className="text-slate-600 mb-6">
                We've received your Go Live request. Our team will review it and contact you within 1-2 business days.
              </p>
              <h4 className="font-semibold text-slate-900 mb-3">What happens next?</h4>
              <ul className="text-left text-sm text-slate-600 space-y-2 max-w-sm mx-auto">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">1.</span>
                  Our team will review your site
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">2.</span>
                  We'll help you set up your custom domain
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">3.</span>
                  DNS configuration and SSL setup
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">4.</span>
                  Final checks and your site goes live! 🎉
                </li>
              </ul>
              <Button onClick={onClose} className="mt-6">
                Close
              </Button>
            </div>
          ) : (
            <>
              <p className="text-slate-600 mb-6">
                Before we launch your site, let's make sure everything is ready. Check off the items you've completed:
              </p>

              {/* Checklist */}
              <div className="space-y-3 mb-6">
                {GO_LIVE_CHECKLIST.map((item) => (
                  <div key={item.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50">
                    <Checkbox
                      id={item.id}
                      checked={checkedItems.includes(item.id)}
                      onCheckedChange={(checked) => handleCheckChange(item.id, checked)}
                    />
                    <Label htmlFor={item.id} className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                      {item.label}
                    </Label>
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div className="mb-6">
                <Label htmlFor="notes" className="text-sm font-medium text-slate-700 mb-2 block">
                  Additional notes or questions (optional)
                </Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g., I'd like help setting up my domain, or I have questions about..."
                  rows={3}
                />
              </div>

              {/* Progress indicator */}
              <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Checklist Progress</span>
                  <span className="text-sm text-slate-500">{checkedItems.length} of {GO_LIVE_CHECKLIST.length}</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(checkedItems.length / GO_LIVE_CHECKLIST.length) * 100}%` }}
                  />
                </div>
              </div>

              {/* Submit button */}
              <Button 
                onClick={handleSubmit} 
                disabled={submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {submitting ? 'Submitting...' : '🚀 Submit Go Live Request'}
              </Button>

              <p className="text-xs text-slate-500 text-center mt-4">
                Don't worry if you haven't completed everything – we'll help you get there!
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGoLiveModal, setShowGoLiveModal] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    { label: 'Pages', value: stats.pages_count, icon: FileText, color: 'indigo', link: '/pages', tooltip: 'Total web pages created in your workspace' },
    { label: 'Newsletters', value: stats.newsletters_count, icon: Mail, color: 'emerald', link: '/newsletters', tooltip: 'Email campaigns created' },
    { label: 'Media Files', value: stats.media_count, icon: Image, color: 'purple', link: '/media', tooltip: 'Images and files in your library' },
    { label: 'Team Members', value: stats.team_count, icon: Users, color: 'blue', link: '/team', tooltip: 'People with access to this workspace' },
    { label: 'Emails Sent', value: stats.total_emails_sent, icon: TrendingUp, color: 'amber', link: '/newsletters', tooltip: 'Total newsletter emails delivered' },
  ] : [];

  return (
    <div className="flex" data-testid="dashboard-page">
      <Sidebar />
      
      <div className="flex-1 min-w-0 bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Dashboard
              </h1>
              <p className="text-slate-600">Welcome back! Here&apos;s your overview</p>
            </div>
            <div className="flex gap-3">
              <SimpleTooltip content="Request to make your site live">
                <Button 
                  onClick={() => setShowGoLiveModal(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="go-live-button"
                >
                  <Rocket size={18} className="mr-2" />
                  Ready to Go Live
                </Button>
              </SimpleTooltip>
              <SimpleTooltip content="Create a new web page">
                <Link to="/pages/new">
                  <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-page-button">
                    <Plus size={18} className="mr-2" />
                    New Page
                  </Button>
                </Link>
              </SimpleTooltip>
              <SimpleTooltip content="Create a new email newsletter">
                <Link to="/newsletters/new">
                  <Button variant="outline" data-testid="create-newsletter-button">
                    <Plus size={18} className="mr-2" />
                    New Newsletter
                  </Button>
                </Link>
              </SimpleTooltip>
            </div>
          </div>

          <FreemiumOnboarding />

          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-500">Loading stats...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
                {statCards.map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <SimpleTooltip key={stat.label} content={stat.tooltip}>
                      <Link
                        to={stat.link}
                        data-testid={`stat-card-${stat.label.toLowerCase().replace(' ', '-')}`}
                        className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg transition-all"
                      >
                        <div className={`w-12 h-12 rounded-lg bg-${stat.color}-100 flex items-center justify-center mb-4`}>
                          <Icon className={`text-${stat.color}-600`} size={24} />
                        </div>
                        <p className="text-3xl font-bold mb-1">{stat.value}</p>
                        <p className="text-slate-600 text-sm">{stat.label}</p>
                      </Link>
                    </SimpleTooltip>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-slate-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Recent Pages
                  </h3>
                  {stats.recent_pages && stats.recent_pages.length > 0 ? (
                    <div className="space-y-3">
                      {stats.recent_pages.map((page) => (
                        <Link
                          key={page.page_id}
                          to={`/pages/${page.page_id}`}
                          data-testid={`recent-page-${page.page_id}`}
                          className="block p-3 hover:bg-slate-50 rounded-lg transition-colors"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <h4 className="font-medium">{page.title}</h4>
                              <p className="text-sm text-slate-500">
                                {page.status} • {new Date(page.updated_at).toLocaleDateString()}
                              </p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs ${
                              page.status === 'published'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-slate-100 text-slate-700'
                            }`}>
                              {page.status}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-6">No pages yet</p>
                  )}
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Quick Actions
                  </h3>
                  <div className="space-y-2">
                    <Link to="/pages/new" data-testid="quick-action-page">
                      <Button variant="outline" className="w-full justify-start">
                        <FileText size={18} className="mr-3" />
                        Create New Page
                      </Button>
                    </Link>
                    <Link to="/newsletters/new" data-testid="quick-action-newsletter">
                      <Button variant="outline" className="w-full justify-start">
                        <Mail size={18} className="mr-3" />
                        Create Newsletter
                      </Button>
                    </Link>
                    <Link to="/media" data-testid="quick-action-media">
                      <Button variant="outline" className="w-full justify-start">
                        <Image size={18} className="mr-3" />
                        Upload Media
                      </Button>
                    </Link>
                    <Link to="/team" data-testid="quick-action-team">
                      <Button variant="outline" className="w-full justify-start">
                        <Users size={18} className="mr-3" />
                        Invite Team Member
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Go Live Modal */}
      <GoLiveModal isOpen={showGoLiveModal} onClose={() => setShowGoLiveModal(false)} />
    </div>
  );
};

export default Dashboard;
